import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import numbro from 'numbro';

import { WEATeacherPlanPrice } from '../../../../constants/exchangeRates';
import { Images } from '../../../../constants/images';
import { SVGIconType } from '../../../../constants/svgIcon';
import { TranslationLocale } from '../../../../i18n';
import { UiLocale } from '../../../../models/Base';
import { useSessionStore } from '../../../../stores/SessionStore';
import { useUserStore } from '../../../../stores/UserStore';
import { getUiLocale } from '../../../../utils/i18n';
import { getLocalCurrencyByRegion } from '../../../../utils/region';
import { SvgIcon } from '../../../Molecules';

import styles from './WEAReferralRewardDetailsTemplate.module.css';

export const WEAReferralRewardDetailsTemplate: React.FC = () => {
  const { t, i18n } = useTranslation();
  const activeUserId = useSessionStore((store) => store.activeUserId);
  const userProfile = useUserStore((state) =>
    state.getProfile(activeUserId ?? ''),
  );
  const activeRegion = useSessionStore((store) => store.activeRegion);
  const currency = useMemo(() => {
    return userProfile?.localCurrency ?? getLocalCurrencyByRegion(activeRegion);
  }, [activeRegion, userProfile?.localCurrency]);
  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <SvgIcon
          name={SVGIconType.Referral}
          width={80}
          height={80}
          color={'white'}
        />
        <h1 className={styles.headerTitle}>{t('Referral Reward Scheme')}</h1>
        <p className={styles.headerDescription}>
          {t(
            'With the WEA Teacher Membership, we offer an attractive referral reward scheme.',
          )}
        </p>
      </header>

      <div className={styles.info}>
        <section className={styles.referralRewardSection}>
          <SvgIcon
            name={SVGIconType.Person}
            width={80}
            height={80}
            color="#4D6EB4"
            stroke="white"
          />
          <h2 className={styles.referralRewardSectionTitle}>
            {t('Referral Reward')}
          </h2>
          <p className={styles.text}>
            {t(
              'After signing up, you can start referring new members. New members who uses your referral link and completed their membership fee payment will become your Referrals.',
            )}
          </p>
          <p className={styles.text}>
            {t(
              'For each Referral, earn {price1} WEA Cash on their 1st year payment and {price2} on each renewal.',
              {
                price1: `${currency}${numbro(
                  WEATeacherPlanPrice[currency].tier1PurchaseReward,
                ).format({ thousandSeparated: true })}`,
                price2: `${currency}${numbro(
                  WEATeacherPlanPrice[currency].tier2PurchaseReward,
                ).format({ thousandSeparated: true })}`,
              },
            )}
          </p>
        </section>

        <section className={styles.usingWEACashSection}>
          <img
            className={styles.usingWEACashIcon}
            src={Images.WEA_Web_IMG_WEACash01}
          />
          <h2 className={styles.usingWEACashSectionTitle}>
            {t('Using WEA Cash')}
          </h2>
          <p className={styles.text}>
            {t(
              'WEA Cash can be spent to reduce the price of your future WEA course purchases.',
            )}
          </p>
          <p className={styles.text}>
            {t(
              "For example, if you have {price1} in WEA Cash and want to purchase a {price2} course, you'll only need to pay {price1}!",
              {
                price1: `${currency}${numbro(
                  WEATeacherPlanPrice[currency].tier1PurchaseReward,
                ).format({ thousandSeparated: true })}`,
                price2: `${currency}${numbro(
                  WEATeacherPlanPrice[currency].examplePrice,
                ).format({ thousandSeparated: true })}`,
              },
            )}
          </p>
          <p className={styles.text}>
            {t(
              'WEA Cash becomes available one month after the referred member pays their membership fee.',
            )}
          </p>

          <img
            className={styles.usingWEACashBanner}
            src={getReferRewardTimeImage(
              getUiLocale(i18n.language as TranslationLocale),
            )}
          />
        </section>
      </div>

      <section className={`${styles.howToRefer}`}>
        <h2 className={styles.howToReferTitle}>{t('How to Refer')}</h2>
        <div className={styles.step}>
          <img
            className={styles.stepImage}
            src={getHowToRefer01Image(
              getUiLocale(i18n.language as TranslationLocale),
            )}
          />
        </div>
        <div className={styles.step}>
          <p className={styles.stepText}>
            {t('Log in, then click "Referrals".')}
          </p>
        </div>
        <div className={styles.step}>
          <img
            className={styles.stepImage}
            src={getHowToRefer02Image(
              getUiLocale(i18n.language as TranslationLocale),
            )}
          />
        </div>
        <div className={styles.step}>
          <p className={styles.stepText}>
            {t('Click “Share & Refer” and share the link to your friends.')}
          </p>
        </div>
        <div className={styles.step}>
          <img
            className={styles.stepImage}
            src={getHowToRefer03Image(
              getUiLocale(i18n.language as TranslationLocale),
            )}
          />
        </div>
        <div className={styles.step}>
          <p className={styles.stepText}>
            {t(
              'Your friends will be able to join WEA Teacher Membership with a reduced price.',
            )}
          </p>
        </div>
        <div className={styles.step}>
          <img
            className={styles.stepImage}
            src={getHowToRefer04Image(
              getUiLocale(i18n.language as TranslationLocale),
            )}
          />
        </div>
        <div className={styles.step}>
          <p className={styles.stepText}>
            {t(
              'After your referred friends paid the membership fee, you will be eligible for the referral reward and can use it after one month.',
            )}
          </p>
        </div>
      </section>
    </div>
  );
};

const getHowToRefer01Image = (lang: UiLocale) => {
  switch (lang) {
    case UiLocale.CN:
      return Images.WEA_Web_IMG_HowToRefer01_SC;
    case UiLocale.HK:
    case UiLocale.TW:
      return Images.WEA_Web_IMG_HowToRefer01_TC;
    case UiLocale.JP:
      return Images.WEA_Web_IMG_HowToRefer01_JP;
    case UiLocale.US:
    default:
      return Images.WEA_Web_IMG_HowToRefer01_EN;
  }
};

const getHowToRefer02Image = (lang: UiLocale) => {
  switch (lang) {
    case UiLocale.CN:
      return Images.WEA_Web_IMG_HowToRefer02_SC;
    case UiLocale.HK:
    case UiLocale.TW:
      return Images.WEA_Web_IMG_HowToRefer02_TC;
    case UiLocale.JP:
      return Images.WEA_Web_IMG_HowToRefer02_JP;
    case UiLocale.US:
    default:
      return Images.WEA_Web_IMG_HowToRefer02_EN;
  }
};

const getHowToRefer03Image = (lang: UiLocale) => {
  switch (lang) {
    case UiLocale.CN:
      return Images.WEA_Web_IMG_HowToRefer03_SC;
    case UiLocale.HK:
    case UiLocale.TW:
      return Images.WEA_Web_IMG_HowToRefer03_TC;
    case UiLocale.JP:
      return Images.WEA_Web_IMG_HowToRefer03_JP;
    case UiLocale.US:
    default:
      return Images.WEA_Web_IMG_HowToRefer03_EN;
  }
};

const getHowToRefer04Image = (lang: UiLocale) => {
  switch (lang) {
    case UiLocale.CN:
      return Images.WEA_Web_IMG_HowToRefer04_SC;
    case UiLocale.HK:
    case UiLocale.TW:
      return Images.WEA_Web_IMG_HowToRefer04_TC;
    case UiLocale.JP:
      return Images.WEA_Web_IMG_HowToRefer04_JP;
    case UiLocale.US:
    default:
      return Images.WEA_Web_IMG_HowToRefer04_EN;
  }
};

const getReferRewardTimeImage = (lang: UiLocale) => {
  switch (lang) {
    case UiLocale.CN:
      return Images.WEA_Web_IMG_ReferRewardTime_SC;
    case UiLocale.HK:
    case UiLocale.TW:
      return Images.WEA_Web_IMG_ReferRewardTime_TC;
    case UiLocale.JP:
      return Images.WEA_Web_IMG_ReferRewardTime_JP;
    case UiLocale.US:
    default:
      return Images.WEA_Web_IMG_ReferRewardTime_EN;
  }
};
